import React, { useState, useRef, useEffect, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { CompassIcon } from '@atlaskit/logo';
import { Text, Flex, xcss, Anchor } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { announcement_periscope_CompassAnnouncementTask$key } from '@atlassian/jira-relay/src/__generated__/announcement_periscope_CompassAnnouncementTask.graphql';
import { TaskItem } from '../../../../common/ui/task-item';
import { TaskTypeItem } from '../../../../common/ui/task-type';
import { useWorkSuggestionsContainer } from '../../../../services/periscope-container';
import { useWorkSuggestionsAction } from '../../../../services/use-work-suggestions-action';
import { messages } from './messages';

const Item = ({
	componentName,
	senderComponentName,
	targetDate,
}: {
	componentName: string;
	senderComponentName: string;
	targetDate?: string | null;
}) => {
	const { formatMessage, formatDate } = useIntl();

	return (
		<Flex xcss={componentStyles} alignItems="center">
			<Flex xcss={titleStyles} alignItems="center" gap="space.100">
				<Flex alignItems="center">
					<CompassIcon appearance="brand" size="small" />
				</Flex>
				<Flex direction="column" gap="space.050">
					<Flex alignItems="center" gap="space.050">
						<Tooltip
							content={formatMessage(messages.announcementTitleNonFinal, {
								senderName: senderComponentName,
								receiverName: componentName,
								b: (chunks: ReactNode) => chunks,
							})}
							position="top"
						>
							<Text color="color.text" maxLines={2}>
								{formatMessage(messages.announcementTitleNonFinal, {
									senderName: senderComponentName,
									receiverName: componentName,
									b: (chunks: ReactNode) => (
										<Text weight="bold" as="strong">
											{chunks}
										</Text>
									),
								})}
							</Text>
						</Tooltip>
					</Flex>
					{targetDate != null && (
						<Flex xcss={taskInfoItemsWrapperStyle}>
							<Text maxLines={1} color="color.text.subtle" size="small">
								{formatMessage(messages.announcementTargetDateInfoNonFinal, {
									targetDate: formatDate(targetDate, {
										day: 'numeric',
										month: 'long',
										year: 'numeric',
									}),
								})}
							</Text>
						</Flex>
					)}
				</Flex>
			</Flex>
		</Flex>
	);
};

export const CompassAnnouncementTask = ({
	fragmentRef,
}: {
	fragmentRef: announcement_periscope_CompassAnnouncementTask$key;
}) => {
	const { formatMessage } = useIntl();
	const descriptionRef = useRef<HTMLElement>(null);
	const data = useFragment(
		graphql`
			fragment announcement_periscope_CompassAnnouncementTask on WorkSuggestionsCompassAnnouncementTask {
				__typename
				id
				title
				url
				componentName
				description
				senderComponentName
				targetDate
			}
		`,
		fragmentRef,
	);

	const [{ showReactionsButtonForSuggestions }, { hideSuggestionsReactionsButton }] =
		useWorkSuggestionsContainer();
	const [isRenderSeeFullAnnouncement, setIsRenderSeeFullAnnouncement] = useState(false);
	const { snooze, remove } = useWorkSuggestionsAction();

	useEffect(() => {
		if (descriptionRef.current) {
			const descriptionElement = descriptionRef.current;
			if (descriptionElement.scrollHeight > descriptionElement.offsetHeight) {
				setIsRenderSeeFullAnnouncement(true);
			}
		}
	}, []);
	if (data.componentName == null || data.senderComponentName == null) {
		return null;
	}

	return (
		<TaskTypeItem
			onRemove={() => remove({ id: data.id, typename: data.__typename })}
			onSnooze={() => snooze({ id: data.id, typename: data.__typename })}
			showReactionsButton={
				showReactionsButtonForSuggestions[data.id] === true ||
				showReactionsButtonForSuggestions[data.id] == null
			}
			url={data.url}
			onReactionsButtonClick={() => hideSuggestionsReactionsButton(data.id)}
			suggestionType={data.__typename}
			id={data.id}
			actionButtonTitle={formatMessage(messages.actionButtonNonFinal)}
		>
			<TaskItem
				taskInfoContent={
					<Item
						componentName={data.componentName}
						senderComponentName={data.senderComponentName}
						targetDate={data.targetDate}
					/>
				}
			>
				<Flex xcss={CommentTextWrapperStyles} direction="column" columnGap="space.150">
					<Text as="strong" maxLines={1} weight="bold">
						{data.title}
					</Text>
					<Text as="p" maxLines={2} ref={descriptionRef}>
						{data.description}
					</Text>
					{isRenderSeeFullAnnouncement && (
						<Anchor href={data.url}>{formatMessage(messages.viewFullAnnouncementNonFinal)}</Anchor>
					)}
				</Flex>
			</TaskItem>
		</TaskTypeItem>
	);
};

const CommentTextWrapperStyles = xcss({
	padding: 'space.200',
	border: '1px solid',
	borderColor: 'color.border',
	borderRadius: '4px',
});

const taskInfoItemsWrapperStyle = xcss({
	color: 'color.text.subtle',
	lineHeight: '14px',
});

const titleStyles = xcss({
	flex: '1 1 0',
	[media.above.md]: {
		flex: '5 1 0',
	},
});

const componentStyles = xcss({
	maxWidth: '90%',
});
